/**
 * Issue an invite code without a request.
 *
 * Used when an administrator already decided to let someone in. The address does
 * not need an account yet: the code is bound to the mailbox and becomes usable at
 * that person's first sign in.
 */

import { useState } from 'react'
import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'

import { ACTIVATION_CODE_TTL_HOURS_MAX, ACTIVATION_CODE_TTL_HOURS_MIN } from '@shared/constants'

import { ErrorAlert } from '../../components/layout/states'
import { Alert, AlertDescription } from '../../components/ui/alert'
import { Button } from '../../components/ui/button'
import { Card, CardContent } from '../../components/ui/card'
import { Field, FieldGroup } from '../../components/ui/field'
import { Input, Textarea } from '../../components/ui/input'
import { useIssueCode } from '../../hooks/use-admin'
import { AdminPage, adminErrorText } from './shared'

export function AdminCodeNewPage() {
  const { t } = useTranslation()
  const issue = useIssueCode()

  const [email, setEmail] = useState('')
  const [note, setNote] = useState('')
  const [ttlHours, setTtlHours] = useState('')

  // Read as text rather than as an `ApiError`: a request that never reached the
  // server still has to say something instead of leaving the form silent.
  const errorText = adminErrorText(issue.error, t)

  return (
    <AdminPage
      title={t('admin.codeNew.title')}
      description={t('admin.codeNew.description')}
      current={t('admin.nav.codes')}
    >
      <Card>
        <CardContent className="pt-4 sm:pt-5">
          <form
            className="flex flex-col gap-4"
            onSubmit={(event) => {
              event.preventDefault()
              const ttl = Number(ttlHours)
              issue.mutate({
                email: email.trim(),
                note: note.trim().length > 0 ? note.trim() : null,
                ...(Number.isFinite(ttl) && ttl > 0 ? { ttlHours: ttl } : {}),
              })
            }}
          >
            <FieldGroup>
              <Field label={t('admin.codeNew.email')} htmlFor="code-email" required>
                <Input
                  id="code-email"
                  type="email"
                  value={email}
                  autoComplete="off"
                  onChange={(event) => setEmail(event.target.value)}
                />
              </Field>
              <Field
                label={t('admin.codeNew.ttl')}
                htmlFor="code-ttl"
                hint={t('admin.codeNew.ttlHint', {
                  min: ACTIVATION_CODE_TTL_HOURS_MIN,
                  max: ACTIVATION_CODE_TTL_HOURS_MAX,
                })}
              >
                <Input
                  id="code-ttl"
                  type="number"
                  inputMode="numeric"
                  min={ACTIVATION_CODE_TTL_HOURS_MIN}
                  max={ACTIVATION_CODE_TTL_HOURS_MAX}
                  value={ttlHours}
                  placeholder={t('admin.codeNew.ttlPlaceholder')}
                  onChange={(event) => setTtlHours(event.target.value)}
                />
              </Field>
              <Field label={t('admin.codeNew.note')} htmlFor="code-note">
                <Textarea
                  id="code-note"
                  rows={2}
                  maxLength={120}
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                />
              </Field>
            </FieldGroup>

            {errorText ? (
              <ErrorAlert message={errorText} />
            ) : null}

            <Button type="submit" disabled={issue.isPending || email.trim().length === 0}>
              {issue.isPending ? t('admin.codeNew.submitting') : t('admin.codeNew.submit')}
            </Button>
          </form>
        </CardContent>
      </Card>

      {issue.isSuccess && issue.data ? (
        <Alert variant={issue.data.delivered ? 'success' : 'destructive'}>
          <AlertDescription>
            {issue.data.delivered
              ? t('admin.codeNew.issuedDelivered', { suffix: issue.data.code.codeSuffix })
              : t('admin.codeNew.issuedNotDelivered', { suffix: issue.data.code.codeSuffix })}
          </AlertDescription>
        </Alert>
      ) : null}

      <div>
        <Button asChild variant="outline" size="sm">
          <Link to="/admin/codes">{t('admin.codes.back')}</Link>
        </Button>
      </div>
    </AdminPage>
  )
}
